import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Vehicle, ReservationFormData, PaymentBreakdown } from '../types';


interface SelectedCoupon {
  user_coupon_id: number;
  coupon_id: number;
  name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  discount_amount: number; // 실제 할인된 금액
}

interface BookingStore {
  // State
  startDateTime: Date | null;
  endDateTime: Date | null;
  selectedVehicle: Vehicle | null;
  selectedCarClasses: string[];
  selectedFuels: string[];
  reservationData: ReservationFormData | null;
  paymentBreakdown: PaymentBreakdown | null;
  selectedCoupon: SelectedCoupon | null;

  // Actions
  setStartDateTime: (date: Date | null) => void;
  setEndDateTime: (date: Date | null) => void;
  setSelectedVehicle: (vehicle: Vehicle | null) => void;
  setSelectedCarClasses: (carClasses: string[]) => void;
  setSelectedFuels: (fuels: string[]) => void;
  setReservationData: (data: ReservationFormData | null) => void;
  setPaymentBreakdown: (breakdown: PaymentBreakdown | null) => void;
  setSelectedCoupon: (coupon: SelectedCoupon | null) => void;
  reset: () => void;

  // Computed values
  getTotalHours: () => number;
  getExactDuration: () => { hours: number; minutes: number; totalMinutes: number };
}

export const useBookingStore = create<BookingStore>()(
  persist(
    (set, get) => ({
  // Initial state
  startDateTime: null,
  endDateTime: null,
  selectedVehicle: null,
  selectedCarClasses: [],
  selectedFuels: [],
  reservationData: null,
  paymentBreakdown: null,
  selectedCoupon: null,

  // Actions
  setStartDateTime: (date) => set({ startDateTime: date }),
  setEndDateTime: (date) => set({ endDateTime: date }),
  setSelectedVehicle: (vehicle) => set({ selectedVehicle: vehicle }),
  setSelectedCarClasses: (carClasses) => set({ selectedCarClasses: carClasses }),
  setSelectedFuels: (fuels) => set({ selectedFuels: fuels }),
  setReservationData: (data) => set({ reservationData: data }),
  setPaymentBreakdown: (breakdown) => set({ paymentBreakdown: breakdown }),
  setSelectedCoupon: (coupon) => set({ selectedCoupon: coupon }),

  reset: () => set({
    startDateTime: null,
    endDateTime: null,
    selectedVehicle: null,
    selectedCarClasses: [],
    selectedFuels: [],
    reservationData: null,
    paymentBreakdown: null,
    selectedCoupon: null,
  }),

  // Computed values
  getTotalHours: () => {
    const { startDateTime, endDateTime } = get();
    if (!startDateTime || !endDateTime) return 0;

    const diff = endDateTime.getTime() - startDateTime.getTime();
    return Math.ceil(diff / (1000 * 60 * 60)); // Convert to hours
  },

  // Get exact duration in hours and minutes
  getExactDuration: () => {
    const { startDateTime, endDateTime } = get();
    if (!startDateTime || !endDateTime) return { hours: 0, minutes: 0, totalMinutes: 0 };

    const diff = endDateTime.getTime() - startDateTime.getTime();
    const totalMinutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes, totalMinutes };
  },
}),
    {
      name: 'booking-storage',
      // Date 객체를 올바르게 직렬화/역직렬화하기 위한 설정
      partialize: (state) => ({
        startDateTime: state.startDateTime instanceof Date ? state.startDateTime.getTime() : null,
        endDateTime: state.endDateTime instanceof Date ? state.endDateTime.getTime() : null,
        selectedVehicle: state.selectedVehicle,
        selectedCarClasses: state.selectedCarClasses,
        selectedFuels: state.selectedFuels,
        reservationData: state.reservationData,
        paymentBreakdown: state.paymentBreakdown,
        selectedCoupon: state.selectedCoupon,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // timestamp 또는 ISO 문자열을 Date 객체로 변환 (마이그레이션 지원)
          if (typeof state.startDateTime === 'number') {
            state.startDateTime = new Date(state.startDateTime);
          } else if (typeof state.startDateTime === 'string') {
            state.startDateTime = new Date(state.startDateTime);
          }
          if (typeof state.endDateTime === 'number') {
            state.endDateTime = new Date(state.endDateTime);
          } else if (typeof state.endDateTime === 'string') {
            state.endDateTime = new Date(state.endDateTime);
          }
        }
      },
    }
  )
);
